import { CDN } from "Constants";

/**
 * Stable identity for one (maintenance, task, activity) triple, keyed by
 * position. Used as the key into `photoSelection` state everywhere a photo
 * selection is read or written (seeding, toggling, rendering, printing, and
 * the per-equipment ZIP export) so all call sites agree on identity.
 */
export const buildActivityKey = (mIdx, tIdx, aIdx) => `${mIdx}-${tIdx}-${aIdx}`;

/**
 * Stable identity for one (maintenance, task, activity) triple, built from
 * REAL database ids (maintenance document _id, task subdocument _id,
 * activity subdocument _id) rather than array position. Used to persist
 * photo selection (order.informePhotoSelection on the backend) so it
 * survives data reshuffles that would silently break `buildActivityKey`.
 */
export const buildStableActivityKey = (maintenanceId, taskId, activityId) =>
  `${maintenanceId}:${taskId}:${activityId}`;

/**
 * Single source of truth for "which activities does the Informe report
 * actually render, and in what order" — the exact same task/activity
 * filters already duplicated across Report.js's seeding effect and
 * collectSelectedUrls (Keying B: only tasks with at least one
 * visible/undefined activity, only activities with `visible` truthy).
 *
 * For each rendered activity, calls `visit` with both the positional
 * indices (mIdx/tIdx/aIdx, matching `buildActivityKey` everywhere else) and
 * the real database ids (maintenanceId/taskId/activityId, matching
 * `buildStableActivityKey`) — the bridge between the two keying schemes.
 *
 * @param {Array} maintenances - view models from buildMaintenanceViewModels
 * @param {(entry: {m:object,mIdx:number,task:object,tIdx:number,activity:object,aIdx:number,maintenanceId:*,taskId:*,activityId:*}) => void} visit
 */
export const iterateReportActivities = (maintenances, visit) => {
  (maintenances || []).forEach((m, mIdx) => {
    (m.tasks || [])
      .filter((t) =>
        (t.activitys || []).some((a) => a.visible === true || a.visible == undefined)
      )
      .forEach((task, tIdx) => {
        (task.activitys || [])
          .filter((a) => a.visible)
          .forEach((activity, aIdx) => {
            visit({
              m,
              mIdx,
              task,
              tIdx,
              activity,
              aIdx,
              maintenanceId: m.maintenanceId,
              taskId: task._id,
              activityId: activity._id,
            });
          });
      });
  });
};

/**
 * Translates the interactive Informe tab's positional `photoSelection`
 * state (keyed by `buildActivityKey`, values are `Set<url>`) into the
 * stable-ID-keyed payload the backend persists (PUT
 * /order/:id/informe/photo-selection), restricted to `touchedKeys` —
 * positional keys the user actually toggled. Activities that only ever
 * received the auto-seeded DEFAULT selection (never manually touched) are
 * intentionally omitted, so the backend's config-default fallback stays in
 * effect for them.
 *
 * @param {Array} maintenances - view models from buildMaintenanceViewModels
 * @param {Object.<string, Set<string>>} photoSelection
 * @param {Set<string>} touchedKeys - positional keys touched via togglePhoto
 * @returns {Object.<string, string[]>}
 */
export const buildStableSelectionPayload = (maintenances, photoSelection, touchedKeys) => {
  const payload = {};
  iterateReportActivities(maintenances, ({ mIdx, tIdx, aIdx, maintenanceId, taskId, activityId }) => {
    const posKey = buildActivityKey(mIdx, tIdx, aIdx);
    if (!touchedKeys || !touchedKeys.has(posKey)) return;
    if (maintenanceId == null || taskId == null || activityId == null) return;

    const selected = photoSelection ? photoSelection[posKey] : undefined;
    if (!selected) return;

    payload[buildStableActivityKey(maintenanceId, taskId, activityId)] = Array.from(selected);
  });
  return payload;
};

/**
 * Reverse of `buildStableSelectionPayload`: expands a persisted stable-ID
 * -keyed selection (plain object, `{ stableKey: string[] }`) into the
 * positional `Set`-keyed shape `resolveSelectedPhotos` expects, for a
 * snapshot render of `maintenances` — used by the print-only page
 * (`/print/informe/:id`), which has no live browser session to hold
 * `photoSelection` in and must rebuild it from the persisted map instead.
 * An activity with no persisted entry is simply left out of the result, so
 * `resolveSelectedPhotos` falls back to its own config-default behavior.
 *
 * @param {Array} maintenances - view models from buildMaintenanceViewModels
 * @param {Object.<string, string[]>} storedSelection - order.informePhotoSelection
 * @returns {Object.<string, Set<string>>}
 */
export const resolvePersistedPhotoSelection = (maintenances, storedSelection) => {
  const positional = {};
  if (!storedSelection) return positional;

  iterateReportActivities(maintenances, ({ mIdx, tIdx, aIdx, maintenanceId, taskId, activityId }) => {
    if (maintenanceId == null || taskId == null || activityId == null) return;
    const urls = storedSelection[buildStableActivityKey(maintenanceId, taskId, activityId)];
    if (Array.isArray(urls) && urls.length > 0) {
      positional[buildActivityKey(mIdx, tIdx, aIdx)] = new Set(urls);
    }
  });
  return positional;
};

/**
 * Whether `file` was hidden by the user for the given maintenance/task/activity.
 */
export const isHide = (maintenance, indexTask, indexActivity, file) => {
  const hidden = maintenance.tasks[indexTask].activitys[indexActivity].hiddenFile;
  if (!hidden) return false;
  return hidden.some((f) => f == file);
};

const resolveEquipmentState = (code) => {
  switch (code) {
    case 2:
      return { equipmentState: "Con novedad", equipmentStateColor: "#FDC745" };
    case 3:
      return { equipmentState: "Fuera de funcionanmiento", equipmentStateColor: "#FF6467" };
    case 1:
    default:
      return { equipmentState: "Funcionando correctamente", equipmentStateColor: "#5EE9B5" };
  }
};

const resolveReply = (activity) => {
  if (
    activity.replyCorrected &&
    activity.replyCorrected.reply &&
    activity.replyCorrected.reply.length
  ) {
    return { reply: activity.replyCorrected.reply, isAiCorrected: true };
  }
  if (activity?.replies && activity?.replies?.length) {
    return { reply: activity.replies.at(-1).reply, isAiCorrected: false };
  }
  return { reply: activity.reply, isAiCorrected: false };
};

const resolveActivityFiles = (doc, indexTask, indexActivity, activity) => {
  if (!activity.file) return [];
  if (typeof activity.file == "string") {
    // Pre-existing quirk preserved verbatim: this branch treats a string
    // `activity.file` as if it had `.path`/`.name`, which strings do not
    // have. Kept as-is on extraction — not a behavior change.
    return [`${CDN}/${activity.file.path}/${activity.file.name}`];
  }
  const files = activity.file.filter(
    (f) => !isHide(doc, indexTask, indexActivity, f._id)
  );
  return files.map((f) => `${CDN}/${f.path}/${f.name}`);
};

/**
 * Pure extraction of Report.js's `_maintenances` computation: filters
 * maintenances by visibility, then maps each into the per-equipment view
 * model shape consumed by the report presentation (and, per-equipment PDFs).
 */
export const buildMaintenanceViewModels = (maintenances) => {
  const visibles = (maintenances || []).filter(
    (m) => m.visible === true || m.visible == undefined
  );

  return visibles.map((doc) => {
    const observations =
      doc.observationsCorrected && doc.observationsCorrected.reply
        ? doc.observationsCorrected.reply
        : doc.observations;

    const { equipmentState, equipmentStateColor } = resolveEquipmentState(
      doc.equipmentState
    );

    const tasks = doc.tasks
      ? doc.tasks.map((task, indexTask) => {
          const t = { ...task };
          t.activitys = task.activitys.map((activity, indexActivity) => {
            const a = {};
            // Stable subdocument id (present on both v1 embedded activitys
            // and v2's separate `activities` collection) — needed to
            // persist/read manual photo selection by real id instead of
            // fragile array position. See buildStableActivityKey.
            a._id = activity._id;
            a.visible = activity.visible;
            a.name =
              activity.nameCorrected && activity.nameCorrected.reply
                ? activity.nameCorrected.reply
                : activity.name;
            const { reply, isAiCorrected } = resolveReply(activity);
            a.reply = reply;
            a.isAiCorrected = isAiCorrected;
            a.file = resolveActivityFiles(doc, indexTask, indexActivity, activity);
            return a;
          });
          return t;
        })
      : [];

    return {
      observations,
      observationsCorrected: doc.observationsCorrected,
      recomendacionesAI: doc.recomendacionesAI,
      maintenanceId: doc.maintenance,
      // sdd/per-equipment-pdf-reports Phase 3: passthrough of the raw
      // maintenance's start date, so ReportDocument can render "Fecha"
      // from the view model alone (needed for standalone per-equipment
      // PDFs, which only ever receive ONE view model — the consolidated
      // report's date already came from the first visible maintenance,
      // which is unchanged since `visibles` preserves source order).
      start: doc.start,
      equipment: doc && doc.equipment ? doc.equipment.name : "...",
      supply_zone: doc && doc.equipment ? doc.equipment.supply_zone : "...",
      equipmentStateCode: doc?.equipmentState,
      equipmentState,
      equipmentStateColor,
      tasks,
    };
  });
};
